import { Injectable } from '@angular/core';

import {
    ApiFragment,
    CustomErrorType,
    Mh2Entity,
    NotificationType,
    PartialToast,
    PerformedAction,
    Toast
} from '@stylehaven/components';
import { Observable, Subject } from 'rxjs';
import { BaseSandbox } from '../base/base.sandbox.service';

@Injectable()
export abstract class BaseActionsSandbox<T> extends BaseSandbox {
    protected actions$: Subject<PerformedAction<T>> = new Subject<PerformedAction<T>>();
    public performedActions$: Observable<PerformedAction<T>> = this.actions$.asObservable();

    // #region Toasts
    public createAndAddActionSuccessToast(
        messageRoot: string,
        entityType: Mh2Entity | string | null,
        totalFragments: number = 1,
        extraMessages?: string[],
        linkText?: string,
        route?: string,
        translationOptions?: Record<string, string>
    ): Toast {
        const toastOptions: PartialToast = {
            type: NotificationType.Success
        };

        const joinedTranslationOptions: { [key: string]: string | number } = {
            ...translationOptions,
            amount: totalFragments
        };

        const toast: Toast = this.createActionToast(
            toastOptions,
            messageRoot,
            'success',
            entityType,
            joinedTranslationOptions,
            extraMessages,
            linkText,
            route
        );

        this.addToast(toast);
        return toast;
    }

    public createAndAddActionWarningToast(
        messageRoot: string,
        entityType: Mh2Entity | string,
        totalFragments: number = 1,
        extraMessages?: string[],
        linkText?: string,
        route?: string
    ): Toast {
        const toastOptions: PartialToast = {
            type: NotificationType.Warning
        };

        const toast: Toast = this.createActionToast(
            toastOptions,
            messageRoot,
            'warning',
            entityType,
            { amount: totalFragments },
            extraMessages,
            linkText,
            route
        );

        this.addToast(toast);
        return toast;
    }

    public createAndAddActionErrorToast(
        messageRoot: string,
        entityType: Mh2Entity | string,
        totalFragments: number = 1,
        extraMessages?: string[],
        linkText?: string,
        route?: string,
        translationOptions?: Record<string, string>
    ): Toast {
        const toastOptions: PartialToast = {
            type: NotificationType.Error
        };

        const joinedTranslationOptions: { [key: string]: string | number } = {
            ...translationOptions,
            amount: totalFragments
        };

        const toast: Toast = this.createActionToast(
            toastOptions,
            messageRoot,
            'error',
            entityType,
            joinedTranslationOptions,
            extraMessages,
            linkText,
            route
        );

        this.addToast(toast);
        return toast;
    }

    public createActionBusyToast(
        messageRoot: string,
        entityType: Mh2Entity | string,
        totalFragments: number = 1,
        extraMessages?: string[],
        linkText?: string,
        route?: string,
        autoClose: boolean = false,
        autoCloseDelay?: number,
        translationOptions?: Record<string, string>
    ): Toast {
        const toastOptions: PartialToast = {
            type: NotificationType.Update,
            autoClose,
            ...(!!autoCloseDelay && { autoCloseDelay }),
            iconRotate: true,
            icon: 'spinner'
        };

        const joinedTranslationOptions: { [key: string]: string | number } = {
            ...translationOptions,
            amount: totalFragments
        };

        const toast: Toast = this.createActionToast(
            toastOptions,
            messageRoot,
            'busy',
            entityType,
            joinedTranslationOptions,
            extraMessages,
            linkText,
            route
        );

        return toast;
    }

    private createActionToast(
        toastOptions: PartialToast,
        messageRoot: string,
        actionType: string,
        entityType: Mh2Entity | string | null,
        translationOptions: { [key: string]: string | number },
        extraMessages: string[] | undefined,
        linkText: string | undefined,
        route: string | undefined
    ): Toast {
        const translatePath: string = entityType
            ? `${messageRoot}.${actionType}.${entityType}`
            : `${messageRoot}.${actionType}`;
        let toastMessages: string[] = [this.translate(translatePath, translationOptions)];

        if (extraMessages) {
            toastMessages = [...toastMessages, ...extraMessages];
        }

        const actionToast: PartialToast = {
            ...toastOptions,
            message: toastMessages.join(' '),
            ...(route &&
                linkText && { navigationLink: { route }, linkText: this.translate(linkText, translationOptions) })
        };

        return this.toastFactory.create(actionToast);
    }

    public handleCustomErrors(error: CustomErrorType | Error): boolean {
        if (error === CustomErrorType.IndexTimeout) {
            this.addToast(
                this.toastFactory.create({
                    type: NotificationType.Warning,
                    message: this.translate('errors.index-timeout')
                })
            );
            return true;
        }

        return false;
    }

    // #endregion

    // #region Utils

    protected containsConcept(fragments: readonly ApiFragment[]): boolean {
        return fragments.some(fragment => fragment.isConcept());
    }

    protected containsPublished(fragments: readonly ApiFragment[]): boolean {
        return fragments.some(fragment => fragment.isPublished());
    }

    // #endregion
}
